import { Field, Item } from '@sitecore-jss/sitecore-jss-nextjs';
import { deepSearchWithParent } from 'lib/utils/object-utils';
import { getRulesFromJsonValue, getRulesFromStringField } from 'lib/rules/serialization';
import { RuleContext, RuleList } from 'lib/rules/rules';
import { PageProps } from 'src/helpers/Forms/Structure/Page';
import { FormFieldProps } from './FormFieldProps';
import { Affiliate } from 'lib/context/AffiliateContext';

type ItemFieldResult<TValue = unknown> = {
  name: string;
  jsonValue: { value: TValue };
};

const personalizationRulesFieldName = 'fieldPersonalizationRules';

class FieldPersonalizationPlugin {
  exec(props: PageProps, userAffiliate?: Affiliate) {
    // Get all fields with field personalization rules
    const results = deepSearchWithParent<FormFieldProps | Item>(props.fields?.children, (x) => {
      if (x?.fields === undefined) {
        return false;
      }

      if (personalizationRulesFieldName in x.fields) {
        return true;
      }

      return false;
    });

    const ruleContext = new RuleContext();
    ruleContext.parameters['userAffiliate'] = userAffiliate;

    results.forEach((res) => {
      const root = res.result?.fields;

      if (!root) {
        return;
      }

      let rules: RuleList<RuleContext> | undefined = undefined;
      if (personalizationRulesFieldName in root) {
        const hideByDefault = root.hideByDefault as Field<boolean>;
        const fieldPersonalizationRules = root.fieldPersonalizationRules as Field<string>;
        rules = getRulesFromStringField(fieldPersonalizationRules);
        ruleContext.parameters['hide'] = hideByDefault.value;
      } else if (
        Array.isArray(root) &&
        root.some((y: ItemFieldResult) => y.name === personalizationRulesFieldName)
      ) {
        const hideByDefault = root.find(
          (y) => y.name === 'hideByDefault'
        ) as ItemFieldResult<boolean>;
        const fieldPersonalizationRules = root.find(
          (y) => y.name === personalizationRulesFieldName
        ) as ItemFieldResult<string>;
        rules = getRulesFromJsonValue(fieldPersonalizationRules.jsonValue);
        ruleContext.parameters['hide'] = hideByDefault.jsonValue.value;
      }

      if (rules === undefined) {
        return;
      }

      rules.runFirstMatching(ruleContext);

      if (ruleContext.parameters['hide'] === true) {
        // If it's inside an array, remove it from the array
        if (Array.isArray(res.parent)) {
          (res.parent as unknown[]).splice(parseInt(res.keyOrIndexAsString!), 1);
        } else {
          // Otherwise remove it directly.
          delete res.parent[res.keyOrIndexAsString!];
        }
      }
    });

    return props;
  }
}

export const fieldPersonalizationPlugin = new FieldPersonalizationPlugin();
